import * as React from 'react';
import { DownloadsPerUserModel, PerDocViewsModelProps } from '../../interfaces';
import { useQuery } from '@apollo/client/react';
import { GET_DOWNLOADS_PER_COMPANY_USER } from '../reports.graphql';
import { Box, Typography, CircularProgress, TextField } from '@mui/material';
import { GeneralExcelExport, GeneralExcelExportProps } from '../brreg';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

function DownloadsPerUser(props: PerDocViewsModelProps) {
  const { orgNumber, fromDate, toDate, productType } = props;

  const { loading, error, data } = useQuery<{ downloadsPerCompanyUser: DownloadsPerUserModel[] }>(GET_DOWNLOADS_PER_COMPANY_USER, {
    variables: {
      orgNumber: orgNumber,
      fromDate: fromDate,
      toDate: toDate,
      productType: productType,
    },
  });
  const [searchQuery, setSearchQuery] = React.useState('');

  if (loading) return <CircularProgress />;
  if (error) return <p>Error :(</p>;

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  let filteredData = data?.downloadsPerCompanyUser;

  // filter data on search keyword
  if (filteredData) {
    filteredData = filteredData.filter((docView: DownloadsPerUserModel) => {
      const query = searchQuery.toLowerCase();
      return docView.firstName.toLowerCase().includes(query) || docView.downloads.toString().includes(query);
    });
  }

  const arrHeading: string[] = ['Navn', 'Antall'];
  const dataSet = filteredData && filteredData.map((x) => [x.firstName + ' ' + x.lastName, x.downloads]);
  const workBook = 'NEDLASTING_ANTALL_PER_BRUKER';
  const fileName = 'nedlastning_per_bruker.xlsx';
  const propsExcel: GeneralExcelExportProps = {
    dataSet: JSON.stringify(dataSet),
    headings: JSON.stringify(arrHeading),
    workBookName: workBook,
    fileName: fileName,
  };

  const handleUserClick = (userId) => {
    const url = `https://salg.byggforsk.no/user/${userId}`;
    window.open(url, '_blank');
  };

  const rows = (filteredData ?? []).map((docView: DownloadsPerUserModel) => ({
    id: docView.userId,
    userId: docView.userId,
    name: `${docView.firstName} ${docView.lastName}`,
    downloads: docView.downloads,
  }));

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'name',
      headerName: 'Navn',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography sx={{ cursor: 'pointer', color: '#287891' }} onClick={() => handleUserClick(params.row.userId)}>
          {params.row.name}
        </Typography>
      ),
    },
    {
      field: 'downloads',
      headerName: 'Antall',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      type: 'number',
    },
  ];

  return (
    <Box sx={{m: 2, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 4}}>
      <Box
        sx={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <TextField
          label="Søk"
          value={searchQuery}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            marginLeft: '16px',
          }}
        />
        {filteredData && <GeneralExcelExport {...propsExcel} />}
      </Box>
      <DataGridPro
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
        sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 } }}
      />
    </Box>
  );
}

export default DownloadsPerUser;
